import Phaser from 'phaser';
import { CHUNK_TILES_W, CHUNK_TILES_H, TILE_H, ELEVATION_PX } from '../config';
import { tileToScreen, screenToTile, isoDepth } from '../iso';
import { ensureTerrainTextures, PROPS } from '../terrain';
import { generateChunk, generateTownChunk, generateDungeonChunk, type Chunk, type NpcSpawn } from '../chunk';
import { findPath, type Point } from '../pathfinding';
import { Unit, VANGUARD_DEF, MARKSMAN_DEF, MEDIC_DEF, MAGE_DEF, ENEMY_DEFS, type UnitDef } from '../units';
import { spawnDamageNumber, spawnAttackArc } from '../fx';
import { Run, runDurationSeconds } from '../runState';
import { showOverlay, hideOverlay, toast } from '../ui/overlays';
import { chromaKeyTextures } from '../spriteFix';
import { Telemetry } from '../telemetry';
import { makeRuntimes, tickStatuses, addStatus, hasStatus, type AbilityRuntime } from '../abilities';
import { loadProfile, recordRunEnd, getConsent, setConsent } from '../persistence';
import { ITEMS as ITEM_DEFS, rollDrop, type EquipSlot } from '../items';
import { showInventory, hideInventory, isInventoryOpen } from '../ui/inventoryPanel';

const SPRITES = ['player', 'goblin', 'skeleton_archer', 'orc_brute'] as const;
const ITEMS = [
  'sword', 'axe', 'mace', 'dagger', 'bow', 'staff',
  'armor_light', 'armor_medium', 'armor_heavy',
  'ring', 'amulet', 'bracers',
] as const;

/** Flavored line from the lorekeeper NPC, picked from run state. */
function lorekeeperLine(run: { distance: number; depth: number; enemiesRouted: number; townsVisited: number }): string {
  if (run.depth > 0) return '"Below the world, the dead have long memories. Bring back nothing you cannot drop."';
  if (run.distance < 3) return '"Walk far enough and every road becomes hostile. Stay watchful."';
  if (run.enemiesRouted > 5) return '"You break their spirits before their bones. Fear is the older blade."';
  if (run.townsVisited >= 3) return '"You\'ve seen three of our walls. The fourth, they say, has no gate at all."';
  return '"Every road leads somewhere. Few lead back."';
}

// Map old chunk-generator sprite slugs onto real item IDs.
const SPRITE_TO_ITEM: Record<string, string> = {
  sword:        'iron_sword',
  axe:          'battle_axe',
  bow:          'longbow',
  mace:         'war_mace',
  ring:         'strength_ring',
  armor_light:  'light_armor',
  armor_medium: 'medium_armor',
  armor_heavy:  'heavy_armor',
  amulet:       'wisdom_amulet',
};
function mapSpriteToItemId(spriteSlug: string): string | null {
  return SPRITE_TO_ITEM[spriteSlug] ?? null;
}

interface LootDrop {
  sprite: Phaser.GameObjects.Image;
  /** Item ID from ITEM_DEFS. Drops on the ground until walked over. */
  itemId: string;
  x: number;
  y: number;
}

export class ExpeditionScene extends Phaser.Scene {
  private chunk!: Chunk;
  private originX = 0;
  private originY = 0;
  private terrainSprites: Phaser.GameObjects.Image[] = [];
  private propSprites: Phaser.GameObjects.Image[] = [];
  private npcSprites: { sprite: Phaser.GameObjects.Image; npc: NpcSpawn }[] = [];
  private dungeonEntranceSprite: Phaser.GameObjects.Image | null = null;
  private party: Unit[] = [];
  private enemies: Unit[] = [];
  private abilities: AbilityRuntime[] = [];
  private titleShown = false;
  private waitingForStart = true;
  private lootItems: LootDrop[] = [];
  private pathPreview!: Phaser.GameObjects.Graphics;
  private exitMarker!: Phaser.GameObjects.Graphics;
  private nextRunId = 0;
  private runEnded = false;
  private worldSeed = 0;

  constructor() { super('ExpeditionScene'); }

  preload(): void {
    for (const s of SPRITES) this.load.image(`sprite_${s}`, `sprites/${s}.png`);
    for (const i of ITEMS) this.load.image(`item_${i}`, `items/${i}.png`);
  }

  create(): void {
    // Scene instances persist across scene.restart() — re-init all mutable
    // state explicitly here, not via field initializers.
    this.terrainSprites = [];
    this.propSprites = [];
    this.npcSprites = [];
    this.dungeonEntranceSprite = null;
    this.party = [];
    this.enemies = [];
    this.lootItems = [];
    this.runEnded = false;
    this.nextRunId = 0;
    this.abilities = makeRuntimes();

    chromaKeyTextures(this, [
      ...SPRITES.map(s => `sprite_${s}`),
      ...ITEMS.map(i => `item_${i}`),
    ]);
    ensureTerrainTextures(this);
    this.worldSeed = Math.floor(Math.random() * 1_000_000);
    Run.reset();
    Telemetry.log('run.start', { seed: this.worldSeed });
    this.buildChunk(Run.get().distance, this.worldSeed);
    if (!this.titleShown) {
      this.titleShown = true;
      this.waitingForStart = true;
      this.showTitleScreen();
    } else {
      this.waitingForStart = false;
    }
    if (!this.scene.isActive('HudScene')) this.scene.launch('HudScene');
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => this.onPointer(p));

    // Hotkeys.
    this.input.keyboard?.on('keydown-ONE', () => this.useHealthPotion());
    this.input.keyboard?.on('keydown-DIGIT1', () => this.useHealthPotion());
    this.input.keyboard?.on('keydown-Q', () => this.tryAbility('cleave'));
    this.input.keyboard?.on('keydown-W', () => this.tryAbility('aimedshot'));
    this.input.keyboard?.on('keydown-E', () => this.tryAbility('mend'));
    this.input.keyboard?.on('keydown-R', () => this.tryAbility('rally'));
    this.input.keyboard?.on('keydown-T', () => this.tryAbility('firebolt'));
    this.input.keyboard?.on('keydown-TWO', () => this.useRation());
    this.input.keyboard?.on('keydown-DIGIT2', () => this.useRation());
    this.input.keyboard?.on('keydown-I', () => this.toggleInventory());

    // Morale cascade: every unit-down ripples to nearby same-faction units.
    const onKilled = (downed: Unit) => this.cascadeMorale(downed);
    this.game.events.on('unit:killed', onKilled);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.game.events.off('unit:killed', onKilled);
    });
  }

  /**
   * When a unit goes down, every living unit of the same faction within the
   * downed unit's spread radius takes a morale hit. Bigger hit for the head
   * of the chain (the slain unit's faction-mates) than for hostiles, which
   * actually GAIN morale from the kill.
   */
  private cascadeMorale(downed: Unit): void {
    const radius = downed.moraleProfile.panicSpreadRadius;
    for (const other of this.allUnits()) {
      if (other === downed) continue;
      const d = other.tileDistanceTo(downed);
      if (other.def.faction === downed.def.faction) {
        if (d <= radius) other.applyMoraleDelta(-15);
      } else {
        // Watching a hostile go down is a rally moment.
        if (d <= 6) other.applyMoraleDelta(+5);
      }
    }
    Telemetry.log('morale.cascade', { down: downed.def.name, faction: downed.def.faction });
  }

  // --- chunk lifecycle -----------------------------------------------------

  private teardownChunk(): void {
    for (const s of this.terrainSprites) s.destroy();
    this.terrainSprites = [];
    for (const p of this.propSprites) p.destroy();
    this.propSprites = [];
    for (const n of this.npcSprites) n.sprite.destroy();
    this.npcSprites = [];
    this.dungeonEntranceSprite?.destroy();
    this.dungeonEntranceSprite = null;
    for (const e of this.enemies) e.destroy();
    this.enemies = [];
    for (const l of this.lootItems) l.sprite.destroy();
    this.lootItems = [];
    this.exitMarker?.destroy();
    this.pathPreview?.destroy();
  }

  /** Party leader — the unit the click controls. Falls back to first living member. */
  private get player(): Unit {
    return this.party[0];
  }

  /** Map role → UnitDef. Roster lives in Run state; this is just the lookup. */
  private defForRole(role: 'vanguard' | 'marksman' | 'medic' | 'mage'): Omit<UnitDef, 'id'> {
    switch (role) {
      case 'vanguard': return VANGUARD_DEF;
      case 'marksman': return MARKSMAN_DEF;
      case 'medic':    return MEDIC_DEF;
      case 'mage':     return MAGE_DEF;
    }
  }

  /** Sync the live party Units back into Run.roster (called before teardown). */
  private snapshotRosterFromParty(): void {
    const roster = Run.get().roster;
    for (const u of this.party) {
      const role = u.def.role;
      if (!role) continue;
      const entry = roster.find(r => r.role === role);
      if (!entry) continue;
      entry.hp = u.alive ? u.hp : 0;
      entry.morale = u.morale;
      entry.alive = u.alive;
      entry.equipment = { ...u.equipment };
    }
  }

  private buildChunk(_depth: number, seed: number): void {
    // Snapshot live party back into run roster before destroying sprites.
    if (this.party.length > 0) this.snapshotRosterFromParty();
    for (const p of this.party) p.destroy();
    this.party = [];

    // chunk-kind selection: dungeons override (when player entered one);
    // villages every 3rd overland chunk; otherwise wilderness.
    const inDungeon = Run.get().depth > 0;
    if (inDungeon) {
      this.chunk = generateDungeonChunk({ depth: Run.get().depth, width: CHUNK_TILES_W, height: CHUNK_TILES_H, seed: seed + Run.get().depth * 1009 + 7 });
    } else {
      const distance = Run.get().distance;
      const isVillage = distance > 1 && distance % 3 === 0;
      this.chunk = isVillage
        ? generateTownChunk({ depth: distance, width: CHUNK_TILES_W, height: CHUNK_TILES_H, seed: seed + distance * 1009 })
        : generateChunk({ depth: distance, width: CHUNK_TILES_W, height: CHUNK_TILES_H, seed: seed + distance * 1009 });
    }

    // The diamond's left edge starts at -chunkH * (TILE_W/2). Offset originX
    // by that amount so all tile world-coords are non-negative — simpler for
    // camera bounds.
    const cw = this.chunk.width;
    const ch = this.chunk.height;
    this.originX = ch * 32;
    this.originY = TILE_H * 2;
    // Camera bounds frame the diamond extents plus a tile of padding.
    const worldMinX = 0;
    const worldMinY = 0;
    const worldMaxX = (cw + ch) * 32;
    const worldMaxY = (cw + ch) * 16 + TILE_H * 4;
    this.cameras.main.setBounds(worldMinX, worldMinY, worldMaxX, worldMaxY);
    this.cameras.main.setBackgroundColor('#0a0a0a');

    this.renderTerrain();
    this.renderProps();
    this.exitMarker = this.add.graphics();
    this.drawExitMarker();

    const start = this.chunk.playerStart;
    const roster = Run.get().roster;
    for (let i = 0; i < roster.length; i++) {
      const entry = roster[i];
      if (!entry.alive) continue; // dead members stay dead this run
      const baseDef = this.defForRole(entry.role);
      const tx = start.x + (i === 0 ? 0 : (i % 2 ? 0 : -1));
      const ty = start.y + (i === 0 ? 0 : (i - 1) % 2 === 0 ? -1 : 1);
      const unit = new Unit({
        scene: this,
        def: { ...baseDef, id: `party_${this.nextRunId++}` },
        tileX: tx,
        tileY: ty,
        originX: this.originX,
        originY: this.originY,
      });
      // Restore HP, morale, equipment from last snapshot.
      unit.hp = entry.hp;
      unit.morale = entry.morale;
      unit.equipment = { ...(entry.equipment ?? {}) } as Partial<Record<EquipSlot, string>>;
      unit.syncSprite();
      this.party.push(unit);
    }

    for (const spawn of this.chunk.enemySpawns) {
      const def = ENEMY_DEFS[spawn.type];
      if (!def) continue;
      const enemy = new Unit({
        scene: this,
        def: { ...def, id: `enemy_${this.nextRunId++}` },
        tileX: spawn.x,
        tileY: spawn.y,
        originX: this.originX,
        originY: this.originY,
      });
      this.enemies.push(enemy);
    }

    // Map old chunk lootSpawns (sprite-named) onto real item IDs via a tiny
    // lookup; this keeps the chunk generator simple but gives the player
    // real items.
    for (const loot of this.chunk.lootSpawns) {
      const id = mapSpriteToItemId(loot.item) ?? 'dagger';
      this.spawnLootDrop(loot.x, loot.y, id);
    }

    this.pathPreview = this.add.graphics();
    this.pathPreview.setDepth(99999);

    // Camera follows the leader (party[0]) — re-bind each chunk since the
    // sprite is freshly created on every buildChunk.
    if (this.party[0]?.sprite) {
      this.cameras.main.startFollow(this.party[0].sprite, true, 0.12, 0.12);
    }

    // Dungeon entrance marker on surface chunks that have one.
    if (this.chunk.dungeonEntrance) this.renderDungeonEntrance();

    // Village arrival: spawn NPC sprites and a town banner; player clicks NPCs
    // to talk. No auto-popup — gives the place a sense of presence.
    if (this.chunk.kind === 'village') {
      this.renderNpcs();
      Run.get().townsVisited++;
      Telemetry.log('town.visit', { name: this.chunk.townName ?? 'town', distance: Run.get().distance });
      toast(`You arrive in ${this.chunk.townName ?? 'a town'}.`);
    }
    // Dungeon arrival.
    if (this.chunk.kind === 'dungeon') {
      Telemetry.log('dungeon.enter', { depth: Run.get().depth, biome: this.chunk.biome });
      toast(`You descend into the ${this.chunk.biome}. (Depth ${Run.get().depth})`);
    }

    // Notify HUD.
    this.game.events.emit('hud:update');
  }

  private spawnLootDrop(x: number, y: number, itemId: string): void {
    const def = ITEM_DEFS[itemId];
    if (!def) return;
    const { sx, sy } = tileToScreen(x, y);
    const sprite = this.add.image(this.originX + sx, this.originY + sy - 8, def.spriteKey);
    sprite.setDisplaySize(22, 22);
    sprite.setDepth(isoDepth(x, y, 5));
    this.lootItems.push({ sprite, itemId, x, y });
    this.tweens.add({
      targets: sprite, y: sprite.y - 4, duration: 900,
      yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
  }

  private renderNpcs(): void {
    if (!this.chunk.npcs) return;
    for (const npc of this.chunk.npcs) {
      const { sx, sy } = tileToScreen(npc.x, npc.y);
      const sprite = this.add.image(this.originX + sx, this.originY + sy - 18, 'sprite_player');
      sprite.setDisplaySize(36, 36);
      sprite.setDepth(isoDepth(npc.x, npc.y, 80));
      // Role tint: vendor=yellow, innkeep=blue, recruiter=red, lorekeeper=purple.
      const tint = npc.role === 'vendor' ? 0xfff0a0
        : npc.role === 'innkeep' ? 0x9ad6ff
        : npc.role === 'recruiter' ? 0xff9a9a
        : 0xc89aff;
      sprite.setTint(tint);
      // Name floater above.
      const label = this.add.text(this.originX + sx, this.originY + sy - 44, npc.name, {
        fontFamily: 'monospace', fontSize: '10px', color: '#ffe88a',
        stroke: '#000', strokeThickness: 3,
      }).setOrigin(0.5, 1).setDepth(isoDepth(npc.x, npc.y, 90));
      this.npcSprites.push({ sprite, npc });
      this.npcSprites.push({ sprite: label as unknown as Phaser.GameObjects.Image, npc });
    }
  }

  private renderDungeonEntrance(): void {
    if (!this.chunk.dungeonEntrance) return;
    const { x, y } = this.chunk.dungeonEntrance;
    const { sx, sy } = tileToScreen(x, y);
    const ent = this.add.image(this.originX + sx, this.originY + sy, 'terrain_wall');
    ent.setOrigin(0.5, 0.66);
    ent.setTint(0x2a1c14);
    ent.setDepth(isoDepth(x, y, 60));
    this.dungeonEntranceSprite = ent;
    const label = this.add.text(this.originX + sx, this.originY + sy - 32, '↓ DUNGEON', {
      fontFamily: 'monospace', fontSize: '11px', color: '#ff8c2a',
      stroke: '#000', strokeThickness: 3,
    }).setOrigin(0.5, 0.5).setDepth(isoDepth(x, y, 65));
    this.tweens.add({ targets: label, alpha: 0.4, duration: 900, yoyo: true, repeat: -1 });
  }

  private renderProps(): void {
    for (const p of this.chunk.props) {
      const style = PROPS[p.kind];
      const z = this.chunk.elevation[p.y]?.[p.x] ?? 0;
      const { sx, sy } = tileToScreen(p.x, p.y);
      const img = this.add.image(
        this.originX + sx,
        this.originY + sy - z * ELEVATION_PX + TILE_H / 2,
        style.texKey,
      );
      img.setOrigin(0.5, 1); // anchor at bottom so taller props rise up
      img.setDepth(isoDepth(p.x, p.y, 50));
      this.propSprites.push(img);
    }
  }

  private renderTerrain(): void {
    for (let y = 0; y < this.chunk.height; y++) {
      for (let x = 0; x < this.chunk.width; x++) {
        const kind = this.chunk.terrain[y][x];
        const z = this.chunk.elevation[y][x];
        const { sx, sy } = tileToScreen(x, y);
        const img = this.add.image(this.originX + sx, this.originY + sy - z * ELEVATION_PX, `terrain_${kind}`);
        img.setOrigin(0.5, kind === 'wall' ? 0.66 : 0.5);
        img.setDepth(isoDepth(x, y, -2));
        this.terrainSprites.push(img);
      }
    }
  }

  private drawExitMarker(): void {
    const e = this.chunk.exit;
    const { sx, sy } = tileToScreen(e.x, e.y);
    const ex = this.originX + sx;
    const ey = this.originY + sy;
    this.exitMarker.clear();
    this.exitMarker.lineStyle(2, 0xffd54a, 1);
    this.exitMarker.fillStyle(0xffd54a, 0.22);
    this.exitMarker.beginPath();
    this.exitMarker.moveTo(ex, ey - 16);
    this.exitMarker.lineTo(ex + 32, ey);
    this.exitMarker.lineTo(ex, ey + 16);
    this.exitMarker.lineTo(ex - 32, ey);
    this.exitMarker.closePath();
    this.exitMarker.fillPath();
    this.exitMarker.strokePath();
    this.exitMarker.setDepth(isoDepth(e.x, e.y, 1));

    // Pulse.
    this.tweens.add({
      targets: this.exitMarker,
      alpha: 0.55,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  // --- input ---------------------------------------------------------------

  private worldToTile(wx: number, wy: number): Point {
    const { tx, ty } = screenToTile(wx - this.originX, wy - this.originY);
    return { x: Math.round(tx), y: Math.round(ty) };
  }

  private onPointer(pointer: Phaser.Input.Pointer): void {
    if (this.runEnded || this.waitingForStart) return;
    if (!this.party[0]?.alive) {
      const survivor = this.party.find(p => p.alive);
      if (!survivor) return;
      this.party = [survivor, ...this.party.filter(p => p !== survivor)];
    }
    const leader = this.party[0];
    const goal = this.worldToTile(pointer.worldX, pointer.worldY);

    // Did we click an NPC? Open their dialog.
    const npc = this.chunk.npcs?.find(n => Math.abs(n.x - goal.x) <= 1 && Math.abs(n.y - goal.y) <= 1);
    if (npc) {
      this.openNpcDialog(npc);
      return;
    }
    // Dungeon entrance click → enter.
    const ent = this.chunk.dungeonEntrance;
    if (ent && Math.abs(ent.x - goal.x) <= 1 && Math.abs(ent.y - goal.y) <= 1) {
      this.enterDungeon();
      return;
    }

    const start = { x: leader.tileX, y: leader.tileY };
    const path = findPath(this.chunk, start, goal);
    if (path.length === 0) return;
    leader.setPath(path);
    leader.target = null;
    this.drawPathPreview(start, path);
  }

  private enterDungeon(): void {
    const run = Run.get();
    run.depth = 1;
    Telemetry.log('dungeon.enter.click', { fromBiome: this.chunk.biome, distance: run.distance });
    this.teardownChunk();
    this.buildChunk(0 /* unused for dungeon path */, this.worldSeed);
  }


  private drawPathPreview(start: Point, path: Point[]): void {
    this.pathPreview.clear();
    if (path.length === 0) return;
    this.pathPreview.lineStyle(2, 0xffd54a, 0.7);
    const s0 = tileToScreen(start.x, start.y);
    this.pathPreview.beginPath();
    this.pathPreview.moveTo(this.originX + s0.sx, this.originY + s0.sy);
    for (const p of path) {
      const w = tileToScreen(p.x, p.y);
      this.pathPreview.lineTo(this.originX + w.sx, this.originY + w.sy);
    }
    this.pathPreview.strokePath();
    const last = path[path.length - 1];
    const g = tileToScreen(last.x, last.y);
    this.pathPreview.fillStyle(0xffd54a, 0.9);
    this.pathPreview.fillCircle(this.originX + g.sx, this.originY + g.sy, 4);
  }

  // --- update loop ---------------------------------------------------------

  update(_t: number, deltaMs: number): void {
    if (this.runEnded) return;
    // Sim is paused until the player clicks Begin (and consent toast is handled).
    if (this.waitingForStart) return;
    // Pause the world while the inventory/character sheet is open.
    if (this.inventoryOpen) return;
    const dt = Math.min(deltaMs / 1000, 0.1);

    this.tickMoraleRegen(dt);
    this.tickAbilityCooldowns(dt);
    this.tickStatuses(dt);
    this.tickHunger(dt);

    // Tick every party member.
    for (let i = 0; i < this.party.length; i++) {
      const member = this.party[i];
      if (!member.alive) continue;
      // Followers get follow-AI when they aren't actively pursuing a hostile.
      if (i > 0) this.followerAI(member);
      member.tickMove(dt);
      member.tickCooldown(dt);
      this.autoAttack(member);
      member.yOffset = -(this.chunk.elevation[member.tileY]?.[member.tileX] ?? 0) * ELEVATION_PX;
      member.syncSprite();
    }

    // Run ends only when the whole party is down.
    if (!this.party.some(p => p.alive)) {
      this.onPlayerDeath();
      return;
    }

    if (this.player.path.length === 0) this.pathPreview.clear();

    for (const e of this.enemies) {
      if (!e.alive) continue;
      this.aggressiveAI(e);
      e.tickMove(dt);
      e.tickCooldown(dt);
      e.yOffset = -(this.chunk.elevation[e.tileY]?.[e.tileX] ?? 0) * ELEVATION_PX;
      e.syncSprite();
    }

    // Reap dead enemies.
    // Detect newly-dead party members for morale hooks.
    for (const p of this.party) {
      if (!p.alive && !(p as any)._deathBroadcast) {
        (p as any)._deathBroadcast = true;
        this.game.events.emit('unit:killed', p);
        Telemetry.log('combat.party_down', {
          role: p.def.role ?? 'unknown',
          depth: this.chunk.depth,
          chunkKind: this.chunk.kind,
          partyAliveAfter: this.party.filter(q => q.alive && q !== p).length,
        });
      }
    }

    const dead = this.enemies.filter(e => !e.alive);
    for (const e of dead) {
      Run.get().enemiesKilled++;
      Telemetry.log('combat.kill', { type: e.def.name, depth: this.chunk.depth });
      this.game.events.emit('unit:killed', e);
      this.tweens.add({
        targets: [e.sprite],
        alpha: 0,
        duration: 280,
        onComplete: () => e.destroy(),
      });
      // Real item drop via the loot table.
      const dropped = rollDrop(Math.random, this.chunk.depth);
      if (dropped) this.spawnLootDrop(e.tileX, e.tileY, dropped.id);
      const gold = 4 + Math.floor(Math.random() * 6) + this.chunk.depth * 2;
      Run.get().goldCarried += gold;
      const ep = e.worldPos();
      spawnDamageNumber(this, ep.x, ep.y - 30, `+${gold}g`, '#ffd54a');
    }
    this.enemies = this.enemies.filter(e => e.alive);

    // Loot pickup: any party member walking near a drop adds it to the shared bag.
    for (let i = this.lootItems.length - 1; i >= 0; i--) {
      const l = this.lootItems[i];
      const picker = this.party.find(p =>
        p.alive && Math.abs(p.tileX - l.x) <= 1 && Math.abs(p.tileY - l.y) <= 1);
      if (!picker) continue;
      const def = ITEM_DEFS[l.itemId];
      Run.get().inventory.push(l.itemId);
      Run.get().lootGathered++;
      const wp = picker.worldPos();
      spawnDamageNumber(this, wp.x, wp.y - 30, `+${def?.name ?? l.itemId}`, '#a4d4ff');
      Telemetry.log('loot.pickup', { item: l.itemId, depth: this.chunk.depth });
      l.sprite.destroy();
      this.lootItems.splice(i, 1);
    }

    this.checkExit();

    // HUD refresh whenever player HP changes — cheap to broadcast on each frame.
    this.game.events.emit('hud:update');
  }

  // --- transitions ---------------------------------------------------------

  private checkExit(): void {
    const ex = this.chunk.exit.x;
    const ey = this.chunk.exit.y;
    const close = Math.abs(this.player.tileX - ex) <= 1 && Math.abs(this.player.tileY - ey) <= 1;
    if (!close) return;
    // Only advance when no enemies remain in line of sight (= still on chunk).
    const aliveCount = this.enemies.length;
    if (aliveCount > 0) {
      toast(`${aliveCount} hostile${aliveCount > 1 ? 's' : ''} still on this map — push through!`);
      // Cooldown the toast by moving player slightly back from exit logic.
      this.player.tileX = Math.max(0, this.player.tileX - 1);
      return;
    }
    this.advanceChunk();
  }

  // --- title / persistence -------------------------------------------------

  private showTitleScreen(): void {
    const profile = loadProfile();
    const rows: { label: string; value: string | number }[] = [
      { label: 'Total expeditions', value: profile.totalRuns },
      { label: 'Furthest depth',    value: profile.bestDepth },
      { label: 'Most enemies felled', value: profile.bestKills },
    ];
    if (profile.lastRun) {
      rows.push({ label: 'Last run',  value: `depth ${profile.lastRun.depth}, ${profile.lastRun.kills} kills` });
    }
    showOverlay({
      title: 'WANDERBORNE',
      subtitle: 'An expedition crawler. Push your party forward until the march breaks.',
      flavor: '"Every road leads somewhere. Few lead back."',
      rows,
      buttons: [
        {
          label: 'Begin Expedition',
          primary: true,
          onClick: () => {
            hideOverlay();
            this.waitingForStart = false;
            this.maybeAskConsent();
          },
        },
      ],
    });
  }

  private maybeAskConsent(): void {
    if (getConsent() !== null) return;
    // Light, dismissable consent prompt — telemetry just for play-style insights.
    setTimeout(() => {
      const root = document.createElement('div');
      root.className = 'wb-toast';
      root.style.bottom = '16px';
      root.innerHTML = `
        <span style="margin-right: 10px;">Share anonymous play data to help tune the game?</span>
        <button class="wb-btn wb-btn-primary" style="padding: 4px 10px; font-size: 12px;" id="wb-consent-yes">Yes</button>
        <button class="wb-btn" style="padding: 4px 10px; font-size: 12px; margin-left: 6px;" id="wb-consent-no">No</button>
      `;
      document.body.appendChild(root);
      const yes = root.querySelector('#wb-consent-yes') as HTMLButtonElement;
      const no  = root.querySelector('#wb-consent-no')  as HTMLButtonElement;
      yes.onclick = () => { setConsent(true);  Telemetry.init({ consent: true });  root.remove(); };
      no.onclick  = () => { setConsent(false); Telemetry.init({ consent: false }); root.remove(); };
    }, 800);
  }

  // --- abilities -----------------------------------------------------------

  private tryAbility(id: string): void {
    if (this.runEnded) return;
    const rt = this.abilities.find(a => a.def.id === id);
    if (!rt) return;
    if (rt.remainingCooldown > 0) {
      toast(`${rt.def.name} on cooldown (${rt.remainingCooldown.toFixed(1)}s)`);
      return;
    }
    const caster = this.party.find(p => p.alive && p.def.role === rt.def.ownerRole);
    if (!caster) {
      toast(`No ${rt.def.ownerRole} in party.`);
      return;
    }
    const fired = this.castAbility(rt, caster);
    if (fired) {
      rt.remainingCooldown = rt.def.cooldown;
      Telemetry.log('ability.cast', { id: rt.def.id, role: rt.def.ownerRole });
      this.game.events.emit('hud:update');
    }
  }

  private castAbility(rt: AbilityRuntime, caster: Unit): boolean {
    switch (rt.def.id) {
      case 'cleave': {
        const adjacent: Unit[] = [];
        for (const e of this.enemies) {
          if (!e.alive) continue;
          if (caster.tileDistanceTo(e) <= 1.5) adjacent.push(e);
        }
        if (adjacent.length === 0) { toast('No enemies in cleave range.'); return false; }
        for (const target of adjacent) this.resolveAttack(caster, target, { forcedDamageMul: 1.0, forceCrit: false, skipCooldown: true });
        const wp = caster.worldPos();
        spawnDamageNumber(this, wp.x, wp.y - 36, `CLEAVE x${adjacent.length}`, '#ffd54a');
        return true;
      }
      case 'aimedshot': {
        const target = this.nearestHostile(caster, caster.effectiveAttackRange + 2);
        if (!target) { toast('No target in range.'); return false; }
        this.resolveAttack(caster, target, { forcedDamageMul: 2.5, forceCrit: true, skipCooldown: true });
        const wp = caster.worldPos();
        spawnDamageNumber(this, wp.x, wp.y - 36, 'AIMED SHOT', '#ffd54a');
        return true;
      }
      case 'mend': {
        const wounded = this.party.filter(p => p.alive && p.hp < p.def.maxHp)
          .sort((a, b) => a.hp / a.def.maxHp - b.hp / b.def.maxHp);
        if (wounded.length === 0) { toast('No one needs healing.'); return false; }
        const t = wounded[0];
        const heal = 40;
        t.hp = Math.min(t.def.maxHp, t.hp + heal);
        t.syncSprite();
        const wp = t.worldPos();
        spawnDamageNumber(this, wp.x, wp.y - 30, `+${heal}`, '#a4d4ff');
        return true;
      }
      case 'rally': {
        const survivors = this.party.filter(p => p.alive);
        if (survivors.length === 0) return false;
        for (const p of survivors) p.applyMoraleDelta(+25);
        const wp = caster.worldPos();
        spawnDamageNumber(this, wp.x, wp.y - 36, 'RALLY!', '#ffd54a');
        return true;
      }
      case 'firebolt': {
        const target = this.nearestHostile(caster, caster.effectiveAttackRange + 2);
        if (!target) { toast('No target in range.'); return false; }
        // Direct hit at 2× damage, then ignite everyone within 1.5 tiles of the impact.
        this.resolveAttack(caster, target, { forcedDamageMul: 2.0, forceCrit: false, skipCooldown: true });
        let igniteCount = 0;
        for (const e of this.enemies) {
          if (!e.alive) continue;
          const d = Math.max(Math.abs(e.tileX - target.tileX), Math.abs(e.tileY - target.tileY));
          if (d <= 1.5) { addStatus(e, 'burning', 4, 4); igniteCount++; }
        }
        const wp = caster.worldPos();
        spawnDamageNumber(this, wp.x, wp.y - 36, `FIREBOLT! · ${igniteCount} ignited`, '#ff8c2a');
        return true;
      }
      default:
        return false;
    }
  }

  private tickAbilityCooldowns(dt: number): void {
    for (const a of this.abilities) {
      if (a.remainingCooldown > 0) a.remainingCooldown = Math.max(0, a.remainingCooldown - dt);
    }
  }

  private tickStatuses(dt: number): void {
    for (const u of this.allUnits()) {
      const { selfDamage } = tickStatuses(u, dt);
      if (selfDamage > 0) {
        u.takeDamage(selfDamage);
        const wp = u.worldPos();
        spawnDamageNumber(this, wp.x, wp.y - 18, `${selfDamage}`, '#c62828');
      }
    }
  }

  // --- inventory -----------------------------------------------------------

  private inventoryOpen = false;
  private toggleInventory(): void {
    if (isInventoryOpen()) {
      hideInventory();
      this.inventoryOpen = false;
      return;
    }
    this.inventoryOpen = true;
    showInventory({
      getState: () => ({
        party: this.party.map(u => ({
          role: u.def.role ?? '?',
          name: u.def.name,
          alive: u.alive,
          equipment: { ...u.equipment },
          effectiveStats: {
            maxHp: u.effectiveMaxHp,
            hp: u.hp,
            damage: u.effectiveAttackDamage,
            range: u.effectiveAttackRange,
            speed: u.effectiveMoveSpeed,
            armor: u.armor,
          },
        })),
        inventory: [...Run.get().inventory],
      }),
      onEquip: (memberIdx, itemId) => {
        const m = this.party[memberIdx];
        const def = ITEM_DEFS[itemId];
        if (!m || !def || !m.alive) return;
        // Equip into the slot; swap any existing item back to inventory.
        const bag = Run.get().inventory;
        const bagIdx = bag.indexOf(itemId);
        if (bagIdx < 0) return;
        bag.splice(bagIdx, 1);
        const prev = m.equipment[def.slot];
        if (prev) bag.push(prev);
        m.equipment[def.slot] = itemId;
        m.syncSprite();
        Telemetry.log('item.equip', { item: itemId, role: m.def.role ?? 'unknown' });
      },
      onUnequip: (memberIdx, slot) => {
        const m = this.party[memberIdx];
        if (!m) return;
        const id = m.equipment[slot];
        if (!id) return;
        delete m.equipment[slot];
        Run.get().inventory.push(id);
        m.syncSprite();
        Telemetry.log('item.unequip', { item: id, role: m.def.role ?? 'unknown' });
      },
      onClose: () => { this.inventoryOpen = false; },
    });
  }

  private useHealthPotion(): void {
    const run = Run.get();
    if (run.potions <= 0) { toast('No potions.'); return; }
    // Heal the most wounded living member.
    const candidates = this.party.filter(p => p.alive && p.hp < p.def.maxHp);
    if (candidates.length === 0) { toast('No one needs healing.'); return; }
    candidates.sort((a, b) => (a.hp / a.def.maxHp) - (b.hp / b.def.maxHp));
    const target = candidates[0];
    const heal = 35;
    target.hp = Math.min(target.def.maxHp, target.hp + heal);
    target.syncSprite();
    run.potions--;
    Telemetry.log('potion.used', { healed: target.def.name });
    const wp = target.worldPos();
    spawnDamageNumber(this, wp.x, wp.y - 30, `+${heal}`, '#a4d4ff');
  }

  // --- villages: per-NPC dialog --------------------------------------------

  private openNpcDialog(npc: NpcSpawn): void {
    const run = Run.get();
    const COSTS = { potion: 25, ration: 12, inn: 35, medic: 120, mage: 150 };

    const closeBtn = { label: 'Goodbye', primary: true, onClick: () => hideOverlay() };

    type Btn = { label: string; primary?: boolean; onClick: () => void };
    let buttons: Btn[] = [];
    let title = npc.name;
    let subtitle = '';
    let flavor = npc.greeting;
    let rows: { label: string; value: string | number }[] = [
      { label: 'Gold carried', value: `${run.goldCarried}g` },
    ];

    const refresh = () => this.openNpcDialog(npc);

    switch (npc.role) {
      case 'vendor':
        subtitle = 'Trader · weapons, potions, rations';
        rows.push({ label: 'Potions', value: run.potions });
        rows.push({ label: 'Rations', value: run.rations });
        buttons.push({
          label: `Buy Potion · ${COSTS.potion}g`,
          onClick: () => {
            if (run.goldCarried < COSTS.potion) { toast('Not enough gold.'); return; }
            run.goldCarried -= COSTS.potion; run.potions++;
            Telemetry.log('town.buy.potion', { distance: run.distance, goldAfter: run.goldCarried });
            toast(`Bought a potion. (${run.potions} carried)`); refresh();
          },
        });
        buttons.push({
          label: `Buy Ration · ${COSTS.ration}g`,
          onClick: () => {
            if (run.goldCarried < COSTS.ration) { toast('Not enough gold.'); return; }
            run.goldCarried -= COSTS.ration; run.rations++;
            Telemetry.log('town.buy.ration', { distance: run.distance, goldAfter: run.goldCarried });
            toast(`Bought a ration. (${run.rations} carried)`); refresh();
          },
        });
        break;

      case 'innkeep':
        subtitle = 'Innkeep · room, board, recovery';
        rows.push({ label: 'Hunger', value: `${run.hunger} / 100` });
        rows.push({ label: 'Party',  value: `${this.party.filter(p => p.alive).length} / ${this.party.length}` });
        buttons.push({
          label: `Rest · ${COSTS.inn}g (full heal + morale + hunger)`,
          onClick: () => {
            if (run.goldCarried < COSTS.inn) { toast('Not enough gold.'); return; }
            run.goldCarried -= COSTS.inn; run.hunger = 100;
            for (const p of this.party) {
              if (!p.alive) continue;
              p.hp = p.effectiveMaxHp; p.morale = 90; p.syncSprite();
            }
            Telemetry.log('town.inn.rest', { distance: run.distance, goldAfter: run.goldCarried });
            toast('The party rests. Wounds bind, spirits steady.'); refresh();
          },
        });
        break;

      case 'recruiter':
        subtitle = 'Mercenary captain';
        if (!run.medicHired) {
          buttons.push({
            label: `Hire Medic · ${COSTS.medic}g`,
            onClick: () => {
              if (run.goldCarried < COSTS.medic) { toast('Not enough gold.'); return; }
              if (run.roster.length >= 4) { toast('Party is full.'); return; }
              run.goldCarried -= COSTS.medic;
              run.medicHired = true;
              run.roster.push({ role: 'medic', hp: 70, morale: 70, alive: true, equipment: { weapon: 'oak_staff' } });
              const start = this.chunk.playerStart;
              const medic = new Unit({
                scene: this, def: { ...MEDIC_DEF, id: `party_${this.nextRunId++}` },
                tileX: start.x, tileY: start.y + 1, originX: this.originX, originY: this.originY,
              });
              medic.equipment = { weapon: 'oak_staff' };
              this.party.push(medic);
              Telemetry.log('town.hire.medic', { distance: run.distance, goldAfter: run.goldCarried });
              toast('A medic joins your expedition.'); refresh();
            },
          });
        }
        if (!run.mageHired) {
          buttons.push({
            label: `Hire Mage · ${COSTS.mage}g`,
            onClick: () => {
              if (run.goldCarried < COSTS.mage) { toast('Not enough gold.'); return; }
              if (run.roster.length >= 4) { toast('Party is full.'); return; }
              run.goldCarried -= COSTS.mage; run.mageHired = true;
              run.roster.push({ role: 'mage', hp: 55, morale: 75, alive: true, equipment: { weapon: 'oak_staff', amulet: 'wisdom_amulet' } });
              const start = this.chunk.playerStart;
              const mage = new Unit({
                scene: this, def: { ...MAGE_DEF, id: `party_${this.nextRunId++}` },
                tileX: start.x, tileY: start.y - 1, originX: this.originX, originY: this.originY,
              });
              mage.equipment = { weapon: 'oak_staff', amulet: 'wisdom_amulet' };
              this.party.push(mage);
              Telemetry.log('town.hire.mage', { distance: run.distance, goldAfter: run.goldCarried });
              toast('A mage joins your expedition.'); refresh();
            },
          });
        }
        if (run.medicHired && run.mageHired) {
          flavor = '"All my best hands are with you already. Bring them back alive."';
        }
        break;

      case 'lorekeeper':
        subtitle = 'Wandering monk';
        rows = [
          { label: 'Distance traveled', value: run.distance },
          { label: 'Enemies felled',    value: run.enemiesKilled },
          { label: 'Enemies routed',    value: run.enemiesRouted },
          { label: 'Towns visited',     value: run.townsVisited },
        ];
        flavor = lorekeeperLine(run);
        break;
    }

    buttons.push(closeBtn);
    showOverlay({ title, subtitle, flavor, rows, buttons });
  }

  private advanceChunk(): void {
    const run = Run.get();
    run.chunksClearedThisRun++;
    // In a dungeon: depth++ until they hit the exit, then back to surface.
    // On the surface: distance++.
    if (this.chunk.kind === 'dungeon') {
      // After clearing a dungeon chunk, going past exit returns you to surface.
      run.depth = 0;
      Telemetry.log('dungeon.cleared', { distance: run.distance });
      toast('You climb back into daylight.');
    } else {
      run.distance++;
      Telemetry.log('run.advance', { distance: run.distance, hp: this.player.hp });
      toast(`Distance ${run.distance}`);
    }
    for (const p of this.party) if (p.alive) p.applyMoraleDelta(+12);
    this.teardownChunk();
    this.buildChunk(run.distance, this.worldSeed);
  }

  /** Passive morale regen out of combat — keeps numbers bouncing back. */
  private moraleRegenAccum = 0;
  private tickMoraleRegen(dt: number): void {
    this.moraleRegenAccum += dt;
    if (this.moraleRegenAccum < 1.5) return;
    this.moraleRegenAccum = 0;
    const anyHostileNearby = this.party.some(p => p.alive && this.nearestHostile(p, 8));
    if (anyHostileNearby) return;
    for (const p of this.party) if (p.alive) p.applyMoraleDelta(+2);
    // Passive HP regen IF NOT HUNGRY (the brief's hunger rule).
    if (Run.get().hunger > 25) {
      for (const p of this.party) if (p.alive && p.hp < p.def.maxHp) {
        p.hp = Math.min(p.def.maxHp, p.hp + 2);
        p.syncSprite();
      }
    }
  }

  private hungerAccum = 0;
  private tickHunger(dt: number): void {
    this.hungerAccum += dt;
    if (this.hungerAccum < 5) return;
    this.hungerAccum = 0;
    const run = Run.get();
    if (run.hunger > 0) {
      run.hunger = Math.max(0, run.hunger - 1);
    } else {
      // Starving — chip away party morale.
      for (const p of this.party) if (p.alive) p.applyMoraleDelta(-2);
    }
  }

  private useRation(): void {
    const run = Run.get();
    if (run.rations <= 0) { toast('No rations.'); return; }
    if (run.hunger >= 95) { toast('Already well-fed.'); return; }
    run.rations--;
    run.hunger = Math.min(100, run.hunger + 35);
    Telemetry.log('ration.used');
    toast('You take a meal on the march.');
  }

  // --- death ---------------------------------------------------------------

  private onPlayerDeath(): void {
    if (this.runEnded) return;
    this.runEnded = true;
    const run = Run.get();
    const duration = runDurationSeconds(run);
    Telemetry.log('run.end', {
      cause: 'death',
      depth: run.depth,
      chunkKind: this.chunk.kind,
      kills: run.enemiesKilled,
      routed: run.enemiesRouted,
      gold: run.goldCarried,
      potions: run.potions,
      rations: run.rations,
      hunger: run.hunger,
      towns: run.townsVisited,
      partyRoles: this.party.map(p => p.def.role).join(','),
      duration,
    });
    recordRunEnd({
      depth: run.depth,
      kills: run.enemiesKilled,
      gold: run.goldCarried,
      durationSeconds: duration,
      cause: 'death',
    });
    showOverlay({
      title: 'The march has broken.',
      subtitle: 'Your expedition perished in hostile country.',
      flavor: '"Every road leads somewhere. Few lead back."',
      rows: [
        { label: 'Distance traveled', value: run.distance },
        { label: 'Deepest dungeon',   value: run.depth },
        { label: 'Chunks cleared',    value: run.chunksClearedThisRun },
        { label: 'Enemies killed',    value: run.enemiesKilled },
        { label: 'Enemies routed',    value: run.enemiesRouted },
        { label: 'Loot gathered',     value: run.lootGathered },
        { label: 'Gold carried',      value: run.goldCarried },
        { label: 'Towns visited',     value: run.townsVisited },
        { label: 'Run duration',      value: `${runDurationSeconds(run)}s` },
      ],
      buttons: [
        {
          label: 'Begin a new expedition',
          primary: true,
          onClick: () => {
            hideOverlay();
            this.scene.restart();
            this.runEnded = false;
          },
        },
      ],
    });
  }

  // --- combat AI (faction-general) -----------------------------------------

  /** All living units on both sides — single source of truth for AI scans. */
  private allUnits(): Unit[] {
    const all: Unit[] = [];
    for (const p of this.party) if (p.alive) all.push(p);
    for (const e of this.enemies) if (e.alive) all.push(e);
    return all;
  }

  /** Nearest living enemy of the opposite faction within max range (tile units). */
  private nearestHostile(unit: Unit, maxRange: number): Unit | null {
    let best: Unit | null = null;
    let bestD = Infinity;
    for (const other of this.allUnits()) {
      if (other === unit || other.def.faction === unit.def.faction) continue;
      const d = unit.tileDistanceTo(other);
      if (d <= maxRange && d < bestD) { best = other; bestD = d; }
    }
    return best;
  }

  /** Generic auto-attack: any unit attacks nearest hostile in range. */
  private autoAttack(unit: Unit): void {
    if (!unit.alive || unit.attackCooldown > 0) return;
    const target = this.nearestHostile(unit, unit.effectiveAttackRange);
    if (target) this.resolveAttack(unit, target);
  }

  /**
   * Follower AI: prioritize engaging hostiles in sight; otherwise close on the
   * leader's *destination* — not its current tile — so the goal stays stable
   * while the leader is mid-walk. Re-pathing only kicks in on a new click.
   *
   * We also widen the "close enough" radius with hysteresis so we don't toggle
   * between idle and re-path every other tile.
   */
  private followerAI(unit: Unit): void {
    const sight = unit.def.sightRange ?? unit.effectiveAttackRange + 2;
    const hostile = this.nearestHostile(unit, sight);
    if (hostile) {
      const dist = unit.tileDistanceTo(hostile);
      if (dist <= unit.effectiveAttackRange) {
        unit.path = []; // autoAttack will handle the shot
        return;
      }
      // Even for hostiles, target the current tile but only repath on tile change.
      unit.pathTo(this.chunk, hostile.tileX, hostile.tileY);
      return;
    }
    const leader = this.player;
    if (!leader || leader === unit) return;
    // Aim for the leader's final destination if they're traveling; otherwise
    // their current tile.
    const goal = leader.path.length > 0
      ? leader.path[leader.path.length - 1]
      : { x: leader.tileX, y: leader.tileY };
    const distToGoal = Math.max(Math.abs(unit.tileX - goal.x), Math.abs(unit.tileY - goal.y));
    // Hysteresis: stay idle until we drift past 3 tiles from the goal.
    if (distToGoal <= 2) {
      unit.path = [];
      return;
    }
    unit.pathTo(this.chunk, goal.x, goal.y);
  }

  /** AI for any aggressive unit — currently used by enemies. */
  private aggressiveAI(unit: Unit): void {
    if (!unit.alive) return;

    // Routed: drop weapon and flee to nearest map edge.
    if (unit.isRouted()) {
      this.routeFlee(unit);
      return;
    }

    const sight = unit.def.sightRange ?? unit.effectiveAttackRange + 2;
    const target = this.nearestHostile(unit, sight);
    if (!target) return;
    const dist = unit.tileDistanceTo(target);
    if (dist <= unit.effectiveAttackRange) {
      unit.path = [];
      if (unit.attackCooldown <= 0) this.resolveAttack(unit, target);
      return;
    }
    unit.pathTo(this.chunk, target.tileX, target.tileY);
  }

  private routedNotified = new WeakSet<Unit>();

  private routeFlee(unit: Unit): void {
    // First-time notification for visual & telemetry.
    if (!this.routedNotified.has(unit)) {
      this.routedNotified.add(unit);
      const wp = unit.worldPos();
      spawnDamageNumber(this, wp.x, wp.y - 36, 'ROUTED!', '#ff7777');
      Telemetry.log('morale.routed', { type: unit.def.name });
      Run.get().enemiesRouted++;
    }
    // Find nearest map-edge tile and run toward it.
    const w = this.chunk.width;
    const h = this.chunk.height;
    const candidates = [
      { x: 0,     y: unit.tileY },
      { x: w - 1, y: unit.tileY },
      { x: unit.tileX, y: 0 },
      { x: unit.tileX, y: h - 1 },
    ];
    let best = candidates[0];
    let bestD = Infinity;
    for (const c of candidates) {
      const d = Math.hypot(c.x - unit.tileX, c.y - unit.tileY);
      if (d < bestD) { best = c; bestD = d; }
    }
    unit.pathTo(this.chunk, best.x, best.y);
    // If we made it to an edge tile, despawn (they fled the map).
    if (unit.tileX === best.x && unit.tileY === best.y) {
      unit.alive = false;
      unit.hp = 0;
    }
  }

  private resolveAttack(
    attacker: Unit,
    target: Unit,
    opts?: { forcedDamageMul?: number; forceCrit?: boolean; skipCooldown?: boolean },
  ): void {
    if (!target.alive) return;
    if (!opts?.skipCooldown) attacker.attackCooldown = attacker.effectiveAttackInterval;
    // Morale modulates attack (skipped for ability casts which always fire).
    const band = attacker.moraleBand();
    if (!opts?.forcedDamageMul && Math.random() < band.hesitateChance) {
      const wp = attacker.worldPos();
      spawnDamageNumber(this, wp.x, wp.y - 30, '—', '#9a8f7a');
      return;
    }
    // Concussion debuffs the attacker's outgoing damage.
    const concussedMul = hasStatus(attacker, 'concussed') ? 0.7 : 1.0;
    const swing = 0.8 + Math.random() * 0.4;
    const baseDmg = attacker.effectiveAttackDamage * swing * band.damageMod * concussedMul;
    const isCrit = opts?.forceCrit ?? Math.random() < 0.12;
    const abilityMul = opts?.forcedDamageMul ?? 1.0;
    const dmg = Math.max(1, Math.round(baseDmg * abilityMul * (isCrit ? 1.6 : 1.0)));
    target.takeDamage(dmg);
    if (isCrit) attacker.applyMoraleDelta(+3);

    // Crit weapon trauma — distinctive per weapon kind, per the Brief §7.
    if (isCrit && target.alive) this.applyCritTrauma(attacker, target);

    const ap = attacker.worldPos();
    const tp = target.worldPos();
    spawnAttackArc(this, ap.x, ap.y - 16, tp.x, tp.y - 16,
      attacker.def.faction === 'party' ? 0xffd54a : 0xff6b6b);
    spawnDamageNumber(this, tp.x, tp.y - 24, isCrit ? `${dmg}!` : `${dmg}`,
      isCrit ? '#ffd54a'
      : attacker.def.faction === 'party' ? '#ffe88a' : '#ff7777');
  }

  private applyCritTrauma(attacker: Unit, target: Unit): void {
    switch (attacker.def.weapon) {
      case 'slash': {
        addStatus(target, 'bleed', 4, 6);
        const tp = target.worldPos();
        spawnDamageNumber(this, tp.x, tp.y - 14, 'BLEED', '#c62828');
        Telemetry.log('crit.trauma', { weapon: 'slash', effect: 'bleed' });
        break;
      }
      case 'bludgeon': {
        addStatus(target, 'concussed', 4, 1);
        const tp = target.worldPos();
        spawnDamageNumber(this, tp.x, tp.y - 14, 'CONCUSS', '#8a6e2c');
        Telemetry.log('crit.trauma', { weapon: 'bludgeon', effect: 'concussed' });
        break;
      }
      case 'pierce': {
        addStatus(target, 'slow', 3, 1);
        const tp = target.worldPos();
        spawnDamageNumber(this, tp.x, tp.y - 14, 'IMPALED', '#ffd54a');
        Telemetry.log('crit.trauma', { weapon: 'pierce', effect: 'slow' });
        break;
      }
      case 'magic': {
        // Magic crits just shake morale on top of damage.
        target.applyMoraleDelta(-12);
        Telemetry.log('crit.trauma', { weapon: 'magic', effect: 'morale' });
        break;
      }
    }
  }

  /** Public read for the HUD. */
  getStatus(): {
    depth: number;
    distance: number;
    biome: string;
    chunkKind: string;
    killed: number;
    routed: number;
    gold: number;
    potions: number;
    rations: number;
    hunger: number;
    abilities: { id: string; name: string; hotkey: string; cooldown: number; remaining: number; ownerRole: string; ready: boolean }[];
    anyAlive: boolean;
    party: { name: string; hp: number; maxHp: number; role?: string; morale: number; moraleState: string; moraleColor: number }[];
  } {
    return {
      depth: Run.get().depth,
      distance: Run.get().distance,
      biome: this.chunk?.biome ?? 'plains',
      chunkKind: this.chunk?.kind ?? 'wilderness',
      killed: Run.get().enemiesKilled,
      routed: Run.get().enemiesRouted,
      gold: Run.get().goldCarried,
      potions: Run.get().potions,
      rations: Run.get().rations,
      hunger: Run.get().hunger,
      abilities: this.abilities.map(a => ({
        id: a.def.id,
        name: a.def.name,
        hotkey: a.def.hotkey,
        cooldown: a.def.cooldown,
        remaining: a.remainingCooldown,
        ownerRole: a.def.ownerRole,
        ready: a.remainingCooldown <= 0 && this.party.some(p => p.alive && p.def.role === a.def.ownerRole),
      })),
      anyAlive: this.party.some(p => p.alive),
      party: this.party.map(p => {
        const band = p.moraleBand();
        return {
          name: p.def.name,
          hp: p.hp,
          maxHp: p.def.maxHp,
          role: p.def.role,
          morale: Math.round(p.morale),
          moraleState: band.label,
          moraleColor: band.tint,
        };
      }),
    };
  }
}
